<template>
  <div v-if="unreadNotifications.length > 0" class="fixed top-4 right-4 z-50 space-y-2">
    <div 
      v-for="notification in displayedNotifications" 
      :key="notification.id"
      :class="`rounded-lg shadow-lg p-4 max-w-sm transition-all duration-300 ${getNotificationStyle(notification.type)}`"
    >
      <!-- Notification Header -->
      <div class="flex items-start justify-between">
        <div class="flex items-center space-x-2">
          <span class="text-lg">{{ getNotificationIcon(notification.type) }}</span>
          <h4 class="font-semibold text-sm">{{ notification.title }}</h4>
        </div>
        <button 
          @click="dismissNotification(notification.id)"
          class="text-gray-400 hover:text-gray-600 text-sm"
        >
          ×
        </button>
      </div>

      <!-- Notification Content -->
      <p class="text-sm mt-2 text-gray-700">{{ notification.message }}</p>

      <!-- Day Info -->
      <div class="text-xs text-gray-500 mt-2">
        {{ formatNotificationDate(notification.day).date }}
        <span v-if="formatNotificationDate(notification.day).daysAgo > 0" class="text-gray-400">
          ({{ formatNotificationDate(notification.day).daysAgo }} days ago)
        </span>
      </div>

      <!-- Action Button (if applicable) -->
      <div v-if="notification.chocoboId" class="mt-3">
        <button 
          @click="viewChocobo(notification.chocoboId)"
          class="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
        >
          View Chocobo
        </button>
      </div>
    </div>

    <!-- Show More Button -->
    <div v-if="unreadNotifications.length > maxDisplay" class="text-center">
      <button 
        @click="showAll = !showAll"
        class="text-xs bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
      >
        {{ showAll ? 'Show Less' : `+${unreadNotifications.length - maxDisplay} more` }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, defineEmits } from 'vue'
import { useGameStore } from '@/store/game'
import { GaiaCalendar } from '@/utils/gaiaCalendar'

const emit = defineEmits(['view-chocobo'])
const gameStore = useGameStore()
const showAll = ref(false)
const maxDisplay = 3

const unreadNotifications = computed(() => {
  return (gameStore.notifications || [])
    .filter(n => !n.read && n.type !== 'new_bid' && n.type !== 'bid_accepted') // Exclude bid notifications
    .slice(0, 10) // Latest 10 unread
})

const displayedNotifications = computed(() => {
  if (showAll.value) return unreadNotifications.value
  return unreadNotifications.value.slice(0, maxDisplay)
})

const getNotificationIcon = (type) => {
  const icons = {
    breeding_complete: '🐣',
    breeding_started: '🥚',
    chocobo_retired: '🏆',
    race_win: '🥇',
    level_up: '⬆️',
    new_chocobo: '🆕',
    auction_won: '💰',
    general: '📢'
  }
  return icons[type] || icons.general
}

const getNotificationStyle = (type) => {
  const styles = {
    breeding_complete: 'bg-green-100 border-l-4 border-green-500',
    breeding_started: 'bg-yellow-100 border-l-4 border-yellow-500',
    chocobo_retired: 'bg-purple-100 border-l-4 border-purple-500',
    race_win: 'bg-blue-100 border-l-4 border-blue-500',
    level_up: 'bg-indigo-100 border-l-4 border-indigo-500',
    new_chocobo: 'bg-pink-100 border-l-4 border-pink-500',
    auction_won: 'bg-green-100 border-l-4 border-green-500',
    general: 'bg-gray-100 border-l-4 border-gray-500'
  }
  return styles[type] || styles.general
}

const dismissNotification = (notificationId) => {
  const notification = gameStore.notifications.find(n => n.id === notificationId)
  if (notification) {
    notification.read = true
  }
}

const viewChocobo = (chocoboId) => {
  emit('view-chocobo', chocoboId)
}

const formatNotificationDate = (day) => {
  if (!day) return { date: 'N/A', daysAgo: 0 }
  
  const formattedDate = GaiaCalendar.formatGameDate(day)
  const daysAgo = gameStore.currentDay - day
  
  return {
    date: formattedDate,
    daysAgo: daysAgo
  }
}
</script>